import { App, Button, Checkbox, Form, Input, Modal } from 'antd';
import { useState } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { useRingValidationV1ControllerInvalidateRing } from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { queryClient } from 'technical/react-query/client';

interface Props {
  ringId: string;
  segments: { label: string }[];
}

interface InvalidateRingFormValues {
  segments: string[];
  comment: string;
}

export const InvalidateRingButton = ({ ringId, segments }: Props) => {
  const { t } = useTranslation();
  const { currentConstructionSiteId, user } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site');
  invariant(user, 'No user');

  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<InvalidateRingFormValues>();
  const { mutateAsync: invalidateRing, isPending } =
    useRingValidationV1ControllerInvalidateRing();

  const headers = useRestApiHeaders(Module_Enum.QualityControl);

  const handleSubmit = async (values: InvalidateRingFormValues) => {
    try {
      await invalidateRing({
        body: {
          authorId: user.id,
          comment: values.comment,
          segments: values.segments.map((segment) => ({
            segmentType: segment,
          })),
        },
        pathParams: { constructionSiteId: currentConstructionSiteId, ringId },
        headers,
      });
      message.open({
        type: 'success',
        content: t(
          'qualityControl.ringComplianceReport.detail.invalidateButton.success',
        ),
      });

      setOpen(false);
      form.resetFields();
      await queryClient.invalidateQueries({
        queryKey: [
          'v1',
          'quality',
          currentConstructionSiteId,
          'rings-validation',
        ],
      });
    } catch {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.ringComplianceReport.detail.invalidateButton.error',
        ),
      });
    }
  };

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
  };

  return (
    <>
      <Button danger onClick={() => setOpen(true)}>
        {t('qualityControl.ringComplianceReport.detail.invalidateButton.label')}
      </Button>
      <Modal
        open={open}
        title={t(
          'qualityControl.ringComplianceReport.detail.invalidateButton.modalTitle',
        )}
        okText={t(
          'qualityControl.ringComplianceReport.detail.invalidateButton.confirmOk',
        )}
        okButtonProps={{ danger: true, loading: isPending }}
        onOk={() => form.submit()}
        onCancel={handleCancel}
      >
        <Form<InvalidateRingFormValues>
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item<InvalidateRingFormValues>
            name="segments"
            label={t(
              'qualityControl.ringComplianceReport.detail.invalidateButton.segmentsLabel',
            )}
            rules={[{ required: true, type: 'array', min: 1 }]}
          >
            <Checkbox.Group
              options={segments.map((segment) => ({
                label: segment.label,
                value: segment.label,
              }))}
              style={{ flexDirection: 'column', gap: 8 }}
            />
          </Form.Item>
          <Form.Item<InvalidateRingFormValues>
            name="comment"
            label={t(
              'qualityControl.ringComplianceReport.detail.invalidateButton.commentLabel',
            )}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
